import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { CollectionRole } from '@qkindex/contracts';
import { getCollectionDataStore } from '../collection/collectionData';
import { INACTIVE_CAPACITY_SNAPSHOT } from '../collection/collectionDataStore';

export { CAPACITY_CAPABILITY_REFRESH_MS } from '../collection/collectionDataStore';
export type { CollectionCapacityCapabilityState } from '../collection/collectionDataStore';

export const useCollectionCapacityCapability = ({
  userId,
  collectionId,
  membershipVersion,
  role,
  enabled = true,
}: {
  userId?: string;
  collectionId?: string;
  membershipVersion?: number;
  role?: CollectionRole;
  enabled?: boolean;
}) => {
  const store = userId ? getCollectionDataStore(userId) : undefined;
  const context = useMemo(
    () =>
      collectionId &&
      membershipVersion !== undefined &&
      role &&
      ['owner', 'administrator', 'editor'].includes(role)
        ? { collectionId, membershipVersion, role }
        : undefined,
    [collectionId, membershipVersion, role],
  );
  const subscribe = useCallback(
    (listener: () => void) =>
      store && context && enabled
        ? store.subscribeCapacity(context, listener)
        : () => undefined,
    [store, context, enabled],
  );
  const getSnapshot = useCallback(
    () =>
      store && context && enabled
        ? store.getCapacitySnapshot(context)
        : INACTIVE_CAPACITY_SNAPSHOT,
    [store, context, enabled],
  );
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  const refresh = useCallback(async () => {
    if (store && context && enabled) await store.refreshCapacity(context);
  }, [store, context, enabled]);
  const invalidate = useCallback(() => {
    // Disabled consumers may still invalidate shared state after a mutation.
    if (store && context) store.invalidateCapacity(context);
  }, [store, context]);
  return { ...snapshot, refresh, invalidate };
};
